import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as agencyProviderPriceOverrideActions from '@redux/modules/agencyProviderPriceOverride'

import defaultFormState, { requiredFields } from '@models/agencyProviderPriceOverride'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { AgencyProviderPriceOverrideModel, AgencyProviderPriceOverrideRequestOptions } from '@models/types'

export const generateUrls = (agencyProviderPriceOverride?: Partial<AgencyProviderPriceOverrideModel>) => {
  const { id } = agencyProviderPriceOverride || {}

  return {
    editAgencyProviderPriceOverrideDetailsUrl: `#/systemManager/agencyProviderPriceOverrides/${id}/edit/details`,
    editAgencyProviderPriceOverrideUrl: `#/systemManager/agencyProviderPriceOverrides/${id}/edit/`,
    agencyProviderPriceOverridesIndexUrl: '#/systemManager/agencyProviderPriceOverrides',
  }
}

type CreateAgencyProviderPriceOverrideParams = {
  agencyProviderPriceOverrideParams: Partial<AgencyProviderPriceOverrideModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyProviderPriceOverrideRequestOptions,
}

const createAgencyProviderPriceOverride = (params: CreateAgencyProviderPriceOverrideParams) => {
  const { dispatch, agencyProviderPriceOverrideParams, requestOptions } = params
  const { createAgencyProviderPriceOverride: createFn } = agencyProviderPriceOverrideActions

  return dispatch(createFn(agencyProviderPriceOverrideParams, requestOptions))
}

type UpdateAgencyProviderPriceOverrideParams = {
  agencyProviderPriceOverride: AgencyProviderPriceOverrideModel,
  agencyProviderPriceOverrideParams: Partial<AgencyProviderPriceOverrideModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyProviderPriceOverrideRequestOptions,
}

const updateAgencyProviderPriceOverride = (params: UpdateAgencyProviderPriceOverrideParams) => {
  const {
    dispatch, agencyProviderPriceOverride, agencyProviderPriceOverrideParams, requestOptions,
  } = params
  const { updateAgencyProviderPriceOverride: updateFn } = agencyProviderPriceOverrideActions

  const updatedParams = {
    id: agencyProviderPriceOverride.id,
    ...agencyProviderPriceOverrideParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteAgencyProviderPriceOverrideParams = {
  agencyProviderPriceOverride: DeleteParams<AgencyProviderPriceOverrideModel>,
  dispatch: AppDispatch,
}

const deleteAgencyProviderPriceOverride = (params: DeleteAgencyProviderPriceOverrideParams) => {
  const { dispatch, agencyProviderPriceOverride } = params
  const { deleteAgencyProviderPriceOverride: deleteFn } = agencyProviderPriceOverrideActions

  return dispatch(deleteFn(agencyProviderPriceOverride))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAgencyProviderPriceOverrideForm(
  agencyProviderPriceOverride: Partial<AgencyProviderPriceOverrideModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const agencyProviderPriceOverrideForm = useForm(
    defaultFormState,
    { entity: agencyProviderPriceOverride, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [agencyProviderPriceOverride.id, agencyProviderPriceOverride.cache_key],
  )

  return {
    ...agencyProviderPriceOverrideForm,
  }
}

export const useRelations = (agencyProviderPriceOverride: Partial<AgencyProviderPriceOverrideModel> = {}) => {
  const { agency_id } = agencyProviderPriceOverride

  const { agencies } = useSelector(reduxState => reduxState.entities)

  const agency = agency_id && agencies[agency_id] ? agencies[agency_id] : {}

  return {
    agency,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: AgencyProviderPriceOverrideRequestOptions,
}

function useAgencyProviderPriceOverride(
  initEntity: Partial<AgencyProviderPriceOverrideModel> = {},
  options: Options = {},
) {
  const { performHttpRequests, requestOptions } = options

  const { entity: agencyProviderPriceOverride } = useLatestEntity(initEntity, 'agencyProviderPriceOverrides')
  const { id } = agencyProviderPriceOverride

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency } = useRelations(agencyProviderPriceOverride)

  useReduxAction(
    'agencyProviderPriceOverrides',
    'loadAgencyProviderPriceOverride',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.agencyProviderPriceOverrides)

  return {
    agency,
    callbacks: {
      createAgencyProviderPriceOverride:
        (
          agencyProviderPriceOverrideParams: Partial<AgencyProviderPriceOverrideModel>,
          entityOptions?: AgencyProviderPriceOverrideRequestOptions,
        ) => (
          createAgencyProviderPriceOverride({
            agencyProviderPriceOverrideParams, dispatch, requestOptions: entityOptions,
          })
        ),
      createOrEditAgencyProviderPriceOverride: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAgencyProviderPriceOverrideModal',
        payload: { agencyProviderPriceOverride },
      }),
      deleteAgencyProviderPriceOverride:
        // @ts-expect-error TS2322
        () => deleteAgencyProviderPriceOverride({ dispatch, agencyProviderPriceOverride }),
      updateAgencyProviderPriceOverride:
        (
          agencyProviderPriceOverrideParams: Partial<AgencyProviderPriceOverrideModel>,
          entityOptions?: AgencyProviderPriceOverrideRequestOptions,
        ) => (
          updateAgencyProviderPriceOverride({
            // @ts-expect-error TS2322
            agencyProviderPriceOverride, agencyProviderPriceOverrideParams, dispatch, requestOptions: entityOptions,
          })
        ),
    },
    agencyProviderPriceOverride,
    creating,
    deleting,
    // @ts-expect-error TS2339
    loading: loading && !loadedIds.includes(agencyProviderPriceOverride.id),
    updating,
    urls: generateUrls(agencyProviderPriceOverride),
  }
}

export default useAgencyProviderPriceOverride
